import { Suspense, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useIsFetching } from "@tanstack/react-query";
import { PageLoader } from "@/pages/LoadingScreen";
import { MobileFooter } from "./Footer";
import { NotificationPanel } from "./NotificationPanel";
import { AnimatePresence, motion } from "framer-motion";
import { BottomNav } from "./BottomNav";
import { DesktopShell } from "./DesktopShell";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { useTranslation } from "react-i18next";
import { useApp } from "@/context/AppContext";
import { Bell, ChevronLeft, Settings } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";

const ROLE_BADGE = {
  investor: { label: "Investisseur", cls: "bg-blue-100 text-blue-700" },
  farmer:   { label: "Agriculteur",  cls: "bg-green-100 text-green-700" },
  agent:    { label: "Agent terrain", cls: "bg-amber-100 text-amber-700" },
  merchant: { label: "Commerçant",   cls: "bg-purple-100 text-purple-700" },
} as const;
import { toast } from "sonner";
import logoMombongo from "@/assets/logo-mombongo.png";
import { OnboardingModal } from "./OnboardingModal";
import { OfflineBanner } from "@/components/OfflineBanner";
import { PushPermissionBanner } from "@/components/PushPermissionBanner";

function isDetailRoute(path: string) {
  return /^\/(bourse|market|academia|financement|farm)\/.+/.test(path);
}

function getSectionTitle(path: string, t: (k: string) => string) {
  if (path.startsWith("/bourse")) return t("nav.bourse");
  if (path.startsWith("/market")) return t("nav.market");
  if (path.startsWith("/academia")) return t("nav.academia");
  if (path.startsWith("/financement")) return t("home.actions.finance");
  return "";
}

function MobileNavBar({ onBellClick }: { onBellClick: () => void }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { role } = useApp();
  const badge = ROLE_BADGE[role] ?? ROLE_BADGE.investor;
  const { data: notifData } = useNotifications();
  const unread = notifData?.unreadCount ?? 0;

  if (isDetailRoute(pathname)) {
    return (
      <header className="bg-white/95 backdrop-blur-xl border-b border-gray-100 flex items-center gap-0.5 px-1 pt-[max(0.625rem,env(safe-area-inset-top))] pb-2.5 z-30 flex-shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition"
        >
          <ChevronLeft className="w-5 h-5 text-green-700" strokeWidth={2.5} />
        </button>
        <h1 className="font-display font-semibold text-[16px] text-green-700 flex-1">
          {getSectionTitle(pathname, t)}
        </h1>
        {/* Bell stays visible even on detail pages */}
        <button
          onClick={onBellClick}
          className="relative w-10 h-10 rounded-full hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center transition"
        >
          <Bell className="w-[20px] h-[20px] text-gray-700" strokeWidth={1.8} />
          {unread > 0 && (
            <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
          )}
        </button>
      </header>
    );
  }

  const showSettings = pathname === "/profile";

  const title =
    pathname === "/home" ? "Mombongo" :
    pathname === "/market" ? t("nav.market") :
    pathname === "/bourse" ? t("nav.bourse") :
    pathname === "/academia" ? t("nav.academia") :
    pathname === "/profile" ? t("nav.profile") :
    pathname.startsWith("/financement") ? t("home.actions.finance") :
    "Mombongo";

  const subtitle =
    pathname === "/home" ? t("home.greeting") :
    pathname === "/bourse" ? t("bourse.subtitle") :
    pathname === "/academia" ? t("academia.subtitle") :
    pathname.startsWith("/financement") ? t("financement.subtitle") :
    undefined;

  return (
    <header className="bg-white/95 backdrop-blur-xl border-b border-gray-100 px-4 pt-[max(0.625rem,env(safe-area-inset-top))] pb-2.5 z-30 flex-shrink-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src={logoMombongo} alt="Mombongo" className="h-7 w-auto object-contain" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display font-bold text-[15px] leading-tight text-gray-900">{title}</h1>
              <span className={`text-[9px] font-extrabold rounded-full px-2 py-0.5 ${badge.cls}`}>
                {badge.label}
              </span>
            </div>
            {subtitle && (
              <p className="text-[11px] text-gray-500 leading-none mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          {/* Bell is always visible */}
          <button
            onClick={onBellClick}
            className="relative w-10 h-10 rounded-full hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center transition"
          >
            <Bell className="w-[20px] h-[20px] text-gray-700" strokeWidth={1.8} />
            {unread > 0 && (
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
            )}
          </button>
          {showSettings && (
            <button
              onClick={() => toast.info("Paramètres — bientôt disponible", { duration: 2000 })}
              className="w-10 h-10 rounded-full hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center transition"
            >
              <Settings className="w-[20px] h-[20px] text-gray-700" strokeWidth={1.8} />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

// Returns true while navigating between routes (min 700ms, until queries settle).
// Used to swap <Outlet> with <PageLoader> inside the content area only.
function useNavigationLoading() {
  const { pathname } = useLocation();
  const isFetching = useIsFetching();
  const [loading, setLoading] = useState(false);
  const isFirstMount = useRef(true);
  const canHide = useRef(false);
  const minTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setLoading(false), 250);
  };

  useEffect(() => {
    if (isFirstMount.current) { isFirstMount.current = false; return; }
    canHide.current = false;
    setLoading(true);
    if (minTimer.current) clearTimeout(minTimer.current);
    minTimer.current = setTimeout(() => {
      canHide.current = true;
      if (isFetching === 0) doHide();
    }, 700);
  }, [pathname]);

  useEffect(() => {
    if (loading && canHide.current && isFetching === 0) doHide();
  }, [isFetching, loading]);

  return loading;
}

export function AppShell() {
  const location = useLocation();
  const path = location.pathname;
  const isDesktop = useIsDesktop();
  const [notifOpen, setNotifOpen] = useState(false);
  const navLoading = useNavigationLoading();

  const noChrome =
    path === "/" || path.startsWith("/language") || path.startsWith("/auth");

  if (noChrome) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={path}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </motion.div>
      </AnimatePresence>
    );
  }

  if (isDesktop) {
    return (
      <DesktopShell>
        <AnimatePresence mode="wait">
          <motion.div
            key={path}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            <Suspense fallback={<PageLoader />}>
              <Outlet context={{ isDesktop: true }} />
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </DesktopShell>
    );
  }

  // MOBILE: fixed header + scrollable content + fixed tab bar
  return (
    <div className="h-dvh flex flex-col overflow-hidden bg-app">
      <MobileNavBar onBellClick={() => setNotifOpen(true)} />
      <OfflineBanner />
      <PushPermissionBanner />
      <main className="flex-1 overflow-y-auto overscroll-contain">
        <AnimatePresence mode="wait">
          <motion.div
            key={path}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            {navLoading && <PageLoader />}
            <div className={navLoading ? "hidden" : undefined}>
              <Suspense fallback={<PageLoader />}>
                <Outlet context={{ isDesktop: false }} />
              </Suspense>
            </div>
          </motion.div>
        </AnimatePresence>
        <MobileFooter />
      </main>
      <BottomNav />
      <NotificationPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
      <OnboardingModal />
    </div>
  );
}
